import React from 'react';
import { useLabels, usePrefs } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { templatePropTypes } from '../../utilities/commonPropTypes';
import AspectRatioBox from '../../components/aspect-ratio-box/AspectRatioBox';
import OptimizedImageBackground from '../../components/OptimizedImageBackground';
import {
	ctaButtonFragment,
	detailsButtonFragment,
	offerDetails10x1Fragment
} from '../commonFragments';
import {
	getVehicleImages,
	getIncentiveImage,
	getNoImageLabels,
	getFormattedDollarSign
} from '../../utilities/general.jsx';
import { g } from '../../global-constants';
import './sass/Desktop10x1.scss';

const Desktop10x1 = ({
	applicableVehicle = '',
	carouselPosition,
	couponCode = '',
	ctaButtonData,
	description,
	discountDisplay = '',
	imageUrl,
	incentive,
	isMobile,
	isVisible,
	offerDetails,
	promo,
	title,
	type,
	vehicleImages = [],
	width,
	vehicleBackgroundImage,
	modelOfferBackgroundImage
}) => {
	let template;
	const labels = useLabels();
	const { reduceDollarSignSize } = usePrefs();
	const ctaButtonClasses = setClassNames(['btn-inverse']);
	const ctaModalButtonClasses = setClassNames(['my-4']);

	const detailsButtonClasses = [
		'pr-0',
		'text-decoration-underline',
		'text-transform-none',
		'text-right'
	];

	const displayTextClasses = setClassNames([
		'd-flex',
		'flex-column',
		'justify-content-center',
		'flex-shrink-0'
	]);

	const displayClasses = setClassNames([
		'flex-1',
		'd-flex',
		'flex-row',
		'justify-content-center',
		'flex-shrink-0'
	]);

	const noImageLabels = getNoImageLabels(labels, promo.inventoryCount);

	if ([g.PARTS, g.SERVICE, g.EVENT].includes(type)) {
		template = (
			<div className="d-flex flex-row w-100 bg-neutral-1000">
				<OptimizedImageBackground
					key={type}
					className="w-100 flex-shrink-0 fade-image-wrapper image"
					style={{
						backgroundImage: `url("${imageUrl}")`,
						backgroundPosition: 'center',
						backgroundSize: 'cover',
						backgroundColor: 'black',
						flex: 1
					}}
					width={width / 4}
				/>
				<div
					className={setClassNames([displayTextClasses, 'text'])}
					style={{
						flex: 2
					}}
				>
					<h2>
						{getFormattedDollarSign(title, reduceDollarSignSize)}
					</h2>
					<p>
						{getFormattedDollarSign(
							description,
							reduceDollarSignSize
						)}
					</p>
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-center'
					])}
					style={{
						flex: 0.6
					}}
				>
					{discountDisplay && (
						<div className="font-weight-bold text-capitalize discount">
							{discountDisplay}
						</div>
					)}
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-right',
						'align-items-end',
						'mr-3',
						'bg-neutral-1000'
					])}
					style={{
						flex: 1
					}}
				>
					{offerDetails10x1Fragment({
						couponCode,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						}),
						details: detailsButtonFragment({
							carouselPosition,
							detailsButtonClasses,

							isVisible,
							promo,
							cta: ctaButtonFragment({
								carouselPosition,
								ctaModalButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})
						}),
						labels
					})}
				</div>
			</div>
		);
	}

	if (type && type === g.VEHICLE) {
		template = (
			<OptimizedImageBackground
				className="d-flex flex-row fade-image-wrapper"
				style={{
					backgroundImage: `linear-gradient(270deg, rgba(0, 0, 0, 0.70) 82.88%, rgba(0, 0, 0, 0.00) 100%), url("${vehicleBackgroundImage}")`,
					backgroundPosition: 'left bottom',
					backgroundSize: `${width / 3}px 200%`,
					backgroundRepeat: 'no-repeat',
					flexBasis: `${width}px`
				}}
				width={width}
			>
				<div
					key={type}
					className={displayClasses}
					style={{
						flex: 1,
						alignItems: 'center',
						paddingLeft: '1%',
						paddingRight: '1%'
					}}
				>
					{getVehicleImages(vehicleImages, width / 2, isMobile, 60, {
						title: noImageLabels.title,
						subtitle: noImageLabels.subtitle,
						aspectRatio: 'desktop10x1'
					})}
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'px-5',
						'bg-neutral-1000'
					])}
					style={{
						flex: 2
					}}
				>
					<h2>
						{getFormattedDollarSign(title, reduceDollarSignSize)}
					</h2>
					<p>
						{getFormattedDollarSign(
							description,
							reduceDollarSignSize
						)}
					</p>
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-right',
						'align-items-end',
						'pr-3',
						'bg-neutral-1000'
					])}
					style={{
						flex: 1
					}}
				>
					{offerDetails10x1Fragment({
						couponCode,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						}),
						details: detailsButtonFragment({
							carouselPosition,
							detailsButtonClasses,

							isVisible,
							promo,
							cta: ctaButtonFragment({
								carouselPosition,
								ctaModalButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})
						}),
						labels
					})}
				</div>
			</OptimizedImageBackground>
		);
	}

	if (type && type === g.INCENTIVE) {
		template = (
			<OptimizedImageBackground
				className="d-flex flex-row fade-image-wrapper"
				style={{
					backgroundImage: `linear-gradient(270deg, rgba(0, 0, 0, 0.70) 82.88%, rgba(0, 0, 0, 0.00) 100%), url("${modelOfferBackgroundImage}")`,
					backgroundPosition: 'left bottom',
					backgroundSize: `${width / 3}px 200%`,
					backgroundRepeat: 'no-repeat',
					flexBasis: `${width}px`
				}}
				width={width}
			>
				<div
					key={type}
					className={displayClasses}
					style={{
						flex: 1,
						alignItems: 'center'
					}}
				>
					{getIncentiveImage(
						imageUrl,
						width / 2,
						'', // leave empty unless we have a label other than YMMT
						isMobile,
						65,
						{
							title: noImageLabels.title,
							subtitle: noImageLabels.subtitle,
							aspectRatio: 'desktop10x1'
						}
					)}
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'px-5',
						'bg-neutral-1000'
					])}
					style={{
						flex: 2
					}}
				>
					<h2>{applicableVehicle}</h2>
					<p>{getFormattedDollarSign(title, reduceDollarSignSize)}</p>
					<p>
						{getFormattedDollarSign(
							offerDetails,
							reduceDollarSignSize
						)}
					</p>
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-right',
						'align-items-end',
						'pr-3',
						'bg-neutral-1000',
						'cta-wrapper'
					])}
					style={{
						flex: 1
					}}
				>
					{ctaButtonFragment({
						carouselPosition,
						ctaButtonClasses,
						ctaButtonData,
						isVisible,
						promo
					})}
					{detailsButtonFragment({
						carouselPosition,
						detailsButtonClasses,

						incentive,
						isVisible,
						promo,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaModalButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						})
					})}
				</div>
			</OptimizedImageBackground>
		);
	}

	return <AspectRatioBox>{template}</AspectRatioBox>;
};

Desktop10x1.propTypes = templatePropTypes;

export default Desktop10x1;
